import React from 'react';
import { Link } from 'react-router-dom';
import { Column, type ColumnGroupMeta } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { StatusDotPill } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { deleteSpareDelivery } from '../../../services/mutations';
import { computePurchaseOrderTotals } from '../spare-purchase-orders/purchaseOrderTotals';
import {
  supplyListMoneyTypographyClass,
  SupplyListLineItemCountCell} from '../spareListMoneyDisplay';
import { computeSpareDeliveryLinesStatus, type SpareDeliveryLinesStatus } from './deliveryLineIssues';
import type { SpareDelivery } from './useData';

export const moduleConfig = {
  module: {
    title: 'Deliveries',
    permissionModuleKey: 'spare_deliveries',
    route: {
      list: '/spares/spare-deliveries',
      new: '/spares/spare-deliveries/new',
      detail: (id: number) => `/spares/spare-deliveries/${id}`,
      edit: (id: number) => `/spares/spare-deliveries/${id}/edit`},
    api: {
      list: '/spare-deliveries',
      detail: (id: number) => `/spare-deliveries/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference, notes, case, order, supplier or vessel…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['vessel', 'ref', 'dlvDate', 'caseRef', 'orderRef', 'orderDate', 'deliveryLinesStatus', 'supplier']},
  ],
  filters: {
    fields: [
      {
        key: 'vessel',
        label: 'Vessel',
        type: 'select' as const,
        placeholder: 'All vessels',
        getValue: (item: SpareDelivery) => item.vessel?.vesselName || '',
        options: []},
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: SpareDelivery) => item.ref || '',
        options: []},
      {
        key: 'dlvDate',
        label: 'Delivery (dlv)',
        type: 'date' as const,
        placeholder: 'All delivery dates',
        getValue: (item: SpareDelivery) => item.dlvDate || '',
        options: []},
      {
        key: 'caseRef',
        label: 'Case',
        type: 'select' as const,
        placeholder: 'All cases',
        getValue: (item: SpareDelivery) => item.spareCase?.ref || '',
        options: []},
      {
        key: 'orderRef',
        label: 'Order ref',
        type: 'select' as const,
        placeholder: 'All order refs',
        getValue: (item: SpareDelivery) => item.order?.ref || '',
        options: []},
      {
        key: 'orderDate',
        label: 'Order date',
        type: 'date' as const,
        placeholder: 'All order dates',
        getValue: (item: SpareDelivery) => item.order?.orderDate || '',
        options: []},
      {
        key: 'deliveryLinesStatus',
        label: 'Line status',
        type: 'select' as const,
        placeholder: 'All statuses',
        getValue: (item: SpareDelivery) => computeSpareDeliveryLinesStatus(item.lines),
        options: [
          { value: 'no_lines', label: 'No lines' },
          { value: 'complete', label: 'Complete' },
          { value: 'partial', label: 'Partial' },
          { value: 'pending', label: 'Outstanding' },
        ]},
      {
        key: 'supplier',
        label: 'Supplier',
        type: 'select' as const,
        placeholder: 'All suppliers',
        getValue: (item: SpareDelivery) => item.supplier?.name || '',
        options: []},
    ],
    applyFilter: (
      items: SpareDelivery[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      const dateMatches = (
        rawDate: string | null | undefined,
        values: string[],
        range: { start: Date | null; end: Date | null } | undefined,
      ): boolean => {
        if (range?.start || range?.end) {
          const d = rawDate ? new Date(rawDate) : null;
          if (!d || Number.isNaN(d.getTime())) return false;
          if (range.start && d < range.start) return false;
          if (range.end && d > range.end) return false;
          return true;
        }
        if (!values.length) return true;
        const itemDate = toDateOnly(rawDate);
        return values.some((f) => itemDate.includes(String(f).slice(0, 10)));
      };
      return items.filter((item) => {
        const vessel = asArray(filters.vessel);
        if (vessel.length) {
          const v = (item.vessel?.vesselName || '').toLowerCase();
          if (!vessel.some((f) => v.includes(String(f).toLowerCase()))) return false;
        }

        const ref = asArray(filters.ref);
        if (ref.length) {
          const v = item.ref || '';
          if (!ref.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }

        if (!dateMatches(item.dlvDate, asArray(filters.dlvDate), dateRanges?.dlvDate)) return false;

        const caseRef = asArray(filters.caseRef);
        if (caseRef.length) {
          const c = (item.spareCase?.ref || '').toLowerCase();
          if (!caseRef.some((f) => c.includes(String(f).toLowerCase()))) return false;
        }

        const orderRef = asArray(filters.orderRef);
        if (orderRef.length) {
          const o = (item.order?.ref || '').toLowerCase();
          if (!orderRef.some((f) => o.includes(String(f).toLowerCase()))) return false;
        }

        if (!dateMatches(item.order?.orderDate, asArray(filters.orderDate), dateRanges?.orderDate)) return false;

        const lineStatus = asArray(filters.deliveryLinesStatus);
        if (lineStatus.length) {
          const st = computeSpareDeliveryLinesStatus(item.lines);
          if (!lineStatus.some((f) => st === String(f))) return false;
        }

        const supplier = asArray(filters.supplier);
        if (supplier.length) {
          const s = (item.supplier?.name || '').toLowerCase();
          if (!supplier.some((f) => s.includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    },
    applySearch: (items: SpareDelivery[], searchTerm: string) => {
      const q = searchTerm.toLowerCase();
      return items.filter((item) => {
        const parts = [
          item.ref,
          item.notes,
          item.spareCase?.ref,
          item.order?.ref,
          item.order?.quotation?.ref,
          String(item.id),
          String(item.supplierId),
          item.supplier?.name,
          item.supplier?.code,
          item.vessel?.vesselName,
          item.vessel?.vesselAbrv,
          item.vessel?.imoNumber,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return parts.includes(q);
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New delivery',
        showTitle: false,
        variant: 'primary' as const,
        href: '/spares/spare-deliveries/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    columnDividers: false,
    keyExtractor: (item: SpareDelivery) => item.id,
  },
  emptyStates: {
    noData: 'Create a delivery to get started.',
    noSearchResults: 'No deliveries match your search.',
    noFilterResults: 'No deliveries match the filters.',
  },
  delete: {
    title: 'Delete delivery',
    message: 'Delete this delivery and all its lines?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete delivery.',
    handler: async (id: number) => {
      await deleteSpareDelivery(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  form: {
    new: { title: 'New delivery', subtitle: 'Custom form', submitText: 'Create' },
    edit: { title: 'Edit delivery', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.'},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

/** Path must stay in sync with `moduleConfig.route.caseView` in `spare-cases/config.tsx`. */
function supplyDeliveryCaseIdForView(row: SpareDelivery | undefined): number | null {
  const id = row?.spareCase?.id ?? row?.caseId ?? 0;
  return id > 0 ? id : null;
}

const DeliveryCaseColumnCell: React.FC<{ row: SpareDelivery }> = ({ row }) => {
  const { colors } = useTheme();
  const caseId = supplyDeliveryCaseIdForView(row);
  if (caseId == null) {
    return (
      <div className="max-w-[16rem] py-0.5">
        <span className={cn('text-sm', colors.textSecondary)}>—</span>
      </div>
    );
  }
  const displayRef = row.spareCase?.ref?.trim() || `Case #${caseId}`;
  const href = `/spares/spare-cases/${caseId}/case-view`;
  const iconBtnClass =
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900 dark:text-indigo-400 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300';

  return (
    <div className="flex max-w-[16rem] items-center gap-0.5 py-0.5">
      <Link
        to={href}
        className={iconBtnClass}
        title="Open case view"
        aria-label="Open case view"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </Link>
      <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)} title={displayRef}>
        {displayRef}
      </span>
    </div>
  );
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function deliveryLinesStatusDotClass(status: SpareDeliveryLinesStatus): string {
  switch (status) {
    case 'no_lines':
      return 'bg-slate-400 dark:bg-slate-500';
    case 'complete':
      return 'bg-emerald-500 dark:bg-emerald-400';
    case 'partial':
      return 'bg-amber-500 dark:bg-amber-400';
    case 'pending':
      return 'bg-rose-500 dark:bg-rose-400';
    default:
      return 'bg-slate-400 dark:bg-slate-500';
  }
}

function deliveryLinesStatusLabel(status: SpareDeliveryLinesStatus): string {
  switch (status) {
    case 'no_lines':
      return 'No lines';
    case 'complete':
      return 'Complete';
    case 'partial':
      return 'Partial';
    case 'pending':
      return 'Outstanding';
    default:
      return '—';
  }
}

function deliveryLinesStatusTooltip(status: SpareDeliveryLinesStatus): string {
  switch (status) {
    case 'no_lines':
      return 'No delivery lines on this record.';
    case 'complete':
      return 'All lines are marked delivered with quantities matching the PO.';
    case 'partial':
      return 'Some lines still need delivery or quantity reconciliation.';
    case 'pending':
      return 'Every line still needs delivery or quantity reconciliation.';
    default:
      return 'Line reconciliation status';
  }
}

const PURCHASE_ORDER_COLUMN_GROUP: ColumnGroupMeta = {
  id: 'supply-delivery-purchase-order',
  header: 'Purchase order'};

const PoGroupMissing: React.FC = () => {
  const { colors } = useTheme();
  return (
    <span className={cn('block py-0.5 text-sm', colors.textMuted)} title="No linked purchase order">
      —
    </span>
  );
};

const PurchaseOrderGroupDateCell: React.FC<{
  row: SpareDelivery;
  formatDate: (dateString?: string) => string;
}> = ({ row, formatDate }) => {
  const { colors } = useTheme();
  const po = row.order;
  if (!po) return <PoGroupMissing />;
  const label = po.orderDate ? formatDate(po.orderDate) : '—';
  return (
    <span className={cn('block max-w-[9rem] truncate py-0.5 text-sm tabular-nums', colors.text)} title={label}>
      {label}
    </span>
  );
};

const PurchaseOrderGroupRefCell: React.FC<{ row: SpareDelivery }> = ({ row }) => {
  const { colors } = useTheme();
  const po = row.order;
  if (!po) return <PoGroupMissing />;
  const refLine = po.ref?.trim() ? po.ref.trim() : 'No reference';
  return (
    <span className={cn('block max-w-[11rem] truncate py-0.5 text-sm font-medium', colors.text)} title={refLine}>
      {refLine}
    </span>
  );
};

const PurchaseOrderGroupUsdCell: React.FC<{ row: SpareDelivery }> = ({ row }) => {
  const { colors } = useTheme();
  const po = row.order;
  if (!po) return <PoGroupMissing />;
  const totals = computePurchaseOrderTotals(po);
  const usd = totals.grandTotalUsd;
  const has = usd != null;
  const label = has ? fmtMoney(usd) : '—';
  return (
    <span
      className={cn(
        'block w-full min-w-[6rem] py-0.5 text-right tabular-nums',
        supplyListMoneyTypographyClass('usd', colors, has),
      )}
      title="(Line totals + expenses) × exchange rate, in USD"
    >
      {label}
    </span>
  );
};

const DeliveryLinesStatusCell: React.FC<{ row: SpareDelivery }> = ({ row }) => {
  const status = computeSpareDeliveryLinesStatus(row.lines);
  const label = deliveryLinesStatusLabel(status);
  const dotClass = deliveryLinesStatusDotClass(status);
  return (
    <div className="max-w-[10rem] py-0.5">
      <StatusDotPill
        dotClassName={dotClass}
        title={deliveryLinesStatusTooltip(status)}
      >
        {label}
      </StatusDotPill>
    </div>
  );
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SpareDelivery>[] => [
  {
    key: 'vessel.vesselName',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={
          row?.vessel?.vesselName
            ? row.vessel.vesselName
            : row?.vesselId != null
              ? String(row.vesselId)
              : ''
        }
      />
    )},
  { key: 'ref', header: 'Reference', sortable: true, render: (v, row) => <TextCell value={v ?? row?.ref} /> },
  { key: 'dlvDate', header: 'Delivery (dlv)', sortable: true, render: (v) => formatDate(v) },
  {
    key: 'spareCase.ref',
    header: 'Case',
    sortable: true,
    maxWidth: '16rem',
    render: (_v, row) => (row ? <DeliveryCaseColumnCell row={row} /> : <TextCell value="" />)},
  {
    key: 'order.orderDate',
    header: 'Order date',
    sortable: true,
    maxWidth: '9rem',
    columnGroup: PURCHASE_ORDER_COLUMN_GROUP,
    render: (_v, row) => (row ? <PurchaseOrderGroupDateCell row={row} formatDate={formatDate} /> : <TextCell value="" />)},
  {
    key: 'order.ref',
    header: 'Order ref',
    sortable: true,
    maxWidth: '11rem',
    columnGroup: PURCHASE_ORDER_COLUMN_GROUP,
    render: (_v, row) => (row ? <PurchaseOrderGroupRefCell row={row} /> : <TextCell value="" />)},
  {
    key: 'order.grandTotalUsd',
    header: 'Grand total ($)',
    sortable: false,
    align: 'right',
    maxWidth: '8.5rem',
    columnGroup: PURCHASE_ORDER_COLUMN_GROUP,
    render: (_v, row) => (row ? <PurchaseOrderGroupUsdCell row={row} /> : <TextCell value="" />)},
  {
    key: 'deliveryLinesStatus',
    header: 'Line status',
    sortable: false,
    render: (_v, row) => (row ? <DeliveryLinesStatusCell row={row} /> : <TextCell value="" />)},
  {
    key: 'supplier.name',
    header: 'Supplier',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={
          row?.supplier?.name
            ? row.supplier.name
            : row?.supplierId != null
              ? String(row.supplierId)
              : ''
        }
      />
    )},
  {
    key: 'lines',
    header: 'Lines',
    sortable: true,
    align: 'right',
    render: (_v, row) => (
      <SupplyListLineItemCountCell count={row?._count?.lines ?? row?.lines?.length ?? 0} />
    )},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

const TableActionsCell: React.FC<{
  item: SpareDelivery;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: SpareDelivery,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
